import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, MapPin, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';

const steps = [
  { id: 'basics', label: 'Basics' },
  { id: 'location', label: 'Location' },
  { id: 'media', label: 'Media' },
  { id: 'tickets', label: 'Tickets' }
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center gap-2 mb-8">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className={`flex items-center gap-2 ${
          step.id === currentStep ? 'text-primary' : 
          steps.findIndex(s => s.id === currentStep) > index ? 'text-primary' : 'text-muted-foreground'
        }`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step.id === currentStep 
              ? 'bg-primary text-primary-foreground' 
              : steps.findIndex(s => s.id === currentStep) > index
              ? 'bg-primary/30 text-primary'
              : 'bg-secondary'
          }`}>
            {index + 1}
          </div>
          <span className="hidden md:inline text-sm">{step.label}</span>
        </div>
        {index < steps.length - 1 && (
          <div className="flex-1 h-px bg-border max-w-[60px]" />
        )}
      </React.Fragment>
    ))}
  </div>
);

export function AdminCreateEventLocation() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    venueName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });

  const handleNext = () => {
    navigate('/admin/events/new/media');
  };

  const handleBack = () => {
    navigate('/admin/events/new/basics');
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-2xl mx-auto">
        <Link to="/admin" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Events
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Event Location</h1>
        <p className="text-muted-foreground mb-6">Where will your event take place?</p>

        <StepIndicator currentStep="location" />

        <Card className="bg-card border-border p-6">
          <div className="space-y-6">
            {/* Search */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Search Venue</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Search for a venue or address..."
                  className="bg-secondary border-border pl-10"
                />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-card text-muted-foreground">Or enter manually</span>
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Venue Name</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Enter venue name"
                  value={formData.venueName}
                  onChange={(e) => setFormData({...formData, venueName: e.target.value})}
                  className="bg-secondary border-border pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Street Address</label>
              <Input 
                placeholder="Enter street address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="bg-secondary border-border"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">City</label>
                <Input 
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">State</label>
                <Input 
                  placeholder="State"
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">ZIP Code</label>
                <Input 
                  placeholder="ZIP Code"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Country</label>
                <Input 
                  placeholder="Country"
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-border"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button 
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleNext}
              >
                Continue to Media
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminCreateEventLocation;
